import type { AreaDeeplink, DeeplinkTarget, NodeDeeplink } from '../types/EditorLinkTypes';

// The Map Viewer accepts only these keys in a map-state hash; see kontextlab.embed.js.
const MAP_STATE_KEY = /(^|,)[amp]=/;

// A coordinate carries at most five decimals, and trailing zeros are dropped —
// the exact shape serializeMapState() writes in the Map Viewer (map-init.js).
function formatCoordinate(value: number): string {
  return value.toFixed(5).replace(/\.?0+$/, '');
}

function stripHash(hash: string): string {
  return String(hash ?? '').replace(/^#/, '');
}

export function buildNodeDeeplink(nodeId: number): string {
  return `#p=${Math.trunc(nodeId)}`;
}

export function buildAreaDeeplink(layer: number, x: number, y: number): string {
  const parts = [layer.toFixed(0), formatCoordinate(x), formatCoordinate(y)];
  return `#m=${parts.join('/')}`;
}

export function isMapStateHash(hash: string): boolean {
  const value = stripHash(hash);
  return value === '' || MAP_STATE_KEY.test(value);
}

export function parseDeeplink(hash: string): DeeplinkTarget | null {
  const value = stripHash(hash);
  if (!value) return null;

  const segments = new Map<string, string>();
  value.split(',').forEach((segment) => {
    const [key, raw] = segment.split('=');
    if (key && raw !== undefined) segments.set(key, raw);
  });

  // A node wins over coordinates: the Map Viewer opens the popup and the map
  // position only decides which layer it is shown on.
  const popup = segments.get('p');
  if (popup !== undefined) {
    const nodeId = Number.parseInt(popup, 10);
    if (Number.isNaN(nodeId)) return null;
    return { kind: 'node', nodeId } satisfies NodeDeeplink;
  }

  const position = segments.get('m');
  if (position === undefined) return null;

  const [rawLayer, rawX, rawY] = position.split('/');
  const layer = Number.parseInt(rawLayer, 10);
  const x = Number(rawX);
  const y = Number(rawY);
  if (Number.isNaN(layer) || Number.isNaN(x) || Number.isNaN(y)) return null;
  if (rawX === undefined || rawX === '' || rawY === undefined || rawY === '') return null;

  return { kind: 'area', layer, x, y } satisfies AreaDeeplink;
}

export function describeDeeplink(target: DeeplinkTarget): string {
  return target.kind === 'node'
    ? `#p=${target.nodeId}`
    : buildAreaDeeplink(target.layer, target.x, target.y);
}
